import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  User,
  Mail,
  Shield,
  Building2,
  Key,
  Lock,
  Loader2,
} from 'lucide-react';

export const SettingsPage = () => {
  const { user, hospital } = useAuth();
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);
    try {
      await authAPI.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success('Password changed successfully');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Settings
        </h1>
        <p className="mt-1 text-slate-600">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="animate-fade-in">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-emerald-600" />
                Profile Information
              </CardTitle>
              <CardDescription>Your account details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="h-20 w-20 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-2xl font-bold">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900">
                      {user?.first_name} {user?.last_name}
                    </h3>
                    <p className="text-slate-500">{user?.role_name}</p>
                    {user?.is_super_admin && (
                      <span className="inline-flex items-center gap-1 mt-1 text-xs font-medium text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full">
                        <Shield className="h-3 w-3" />
                        Super Admin
                      </span>
                    )}
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-slate-500 text-sm">Email Address</Label>
                    <div className="flex items-center gap-2 text-slate-900">
                      <Mail className="h-4 w-4 text-slate-400" />
                      {user?.email}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-500 text-sm">Role</Label>
                    <div className="flex items-center gap-2 text-slate-900">
                      <Shield className="h-4 w-4 text-slate-400" />
                      {user?.role_name || 'N/A'}
                    </div>
                  </div>
                  {user?.hospital_name && (
                    <div className="space-y-2">
                      <Label className="text-slate-500 text-sm">Hospital</Label>
                      <div className="flex items-center gap-2 text-slate-900">
                        <Building2 className="h-4 w-4 text-slate-400" />
                        {user?.hospital_name}
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-slate-500 text-sm">Account Status</Label>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        user?.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {user?.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card className="animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5 text-emerald-600" />
                Change Password
              </CardTitle>
              <CardDescription>Update your account password</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="currentPassword"
                      type="password"
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                      className="pl-10"
                      required
                      data-testid="current-password-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      required
                      data-testid="new-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={submitting}
                  data-testid="change-password-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Changing...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Account Info Card */}
          {hospital && (
            <Card className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building2 className="h-5 w-5 text-emerald-600" />
                  Hospital Info
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500">Name</p>
                  <p className="font-medium text-slate-900">{hospital.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Code</p>
                  <p className="font-medium text-slate-900">{hospital.code}</p>
                </div>
                {hospital.email && (
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium text-slate-900">{hospital.email}</p>
                  </div>
                )}
                {hospital.phone && (
                  <div>
                    <p className="text-sm text-slate-500">Phone</p>
                    <p className="font-medium text-slate-900">{hospital.phone}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Quick Help */}
          <Card className="animate-fade-in bg-emerald-50 border-emerald-200" style={{ animationDelay: '0.3s' }}>
            <CardContent className="pt-6">
              <h4 className="font-semibold text-emerald-800 mb-2">Need Help?</h4>
              <p className="text-sm text-emerald-700 mb-4">
                Contact our support team for assistance with your account or system features.
              </p>
              <Button variant="outline" className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-100">
                Contact Support
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
